import {
  Button,
  FlexBox,
  Icon,
  IconButton,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import type {
  BasicFilterControl,
  CustomAttributeFilterControl,
  DashboardFilter,
  FilterControl,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useList, useUpdateEffect } from 'react-use';

import { analyticsActions } from 'analytics/analytics.actions';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { getFilterAssets } from '../../utils/filters/filterAssets';
import { isOptionalOperator } from '../../utils/filters/isOptionalOperator';
import { AnalyticsFilter, AnalyticsFilterChangeCallback } from '../filter';

import styles from './analyticsFilters.module.scss';
import { AnalyticsCustomAttributesFilter } from './components/customAttributes/analyticsCustomAttributesFilter';

type AnalyticsFilters = {
  dashboardFilters: readonly DashboardFilter[];
  isCollapsed?: boolean;
};

export const AnalyticsFilters = memo(function AnalyticsFilters({
  dashboardFilters,
  isCollapsed = false,
}: AnalyticsFilters): JSX.Element {
  const { dashboard, context, filters } = useAnalyticsState();
  const dispatch = useAppDispatch();
  const { customFields: hasCustomFields } = useFeatureFlag(['custom-fields']);

  const [draftFilters, draftFilterSetters] = useList<FilterControl>([
    ...filters,
  ]);

  const isDirty = useMemo(
    () => !isEqual(filters, draftFilters),
    [filters, draftFilters],
  );

  const [readFiltersQuery, { data, isFetching, isError }] =
    analyticsApi.useLazyReadFiltersQuery();

  const readFilters = (): void => {
    const preferCacheValue = true;
    readFiltersQuery({ context, id: dashboard.id }, preferCacheValue);
  };

  // Keeps the draft filters in sync with the filters in the store, in case the store filters
  // are changed externally, for example when filters are normalized between changing dashboards.
  useUpdateEffect(() => {
    if (isDirty) {
      draftFilterSetters.set([...filters]);
    }
  }, [filters]);

  // Fetches the filters data on component mount or when the dashboard context changes.
  useEffect(() => {
    readFilters();
  }, [context]);

  /** Adds, updates or removes a filter from the draft state. */
  const handleChange = useCallback<
    AnalyticsFilterChangeCallback<FilterControl>
  >(
    ({ op, filter }) => {
      const predicate = (draft: FilterControl) =>
        draft.field === filter.field &&
        (filter.field === 'custom_fields' && draft.field === 'custom_fields'
          ? filter.attribute_id === draft.attribute_id
          : true);

      if (op === 'upsert') {
        draftFilterSetters.upsert(predicate, filter);
      } else if (op === 'remove') {
        const index = draftFilters.findIndex(predicate);
        draftFilterSetters.removeAt(index);
      }
    },
    [draftFilterSetters, draftFilters],
  );

  /**
   * Updates the analytics state with the selected filters upon clicking
   * "Apply".
   */
  const handleApply = () => {
    dispatch(analyticsActions.setFilters({ filters: draftFilters }));

    ampli.analyticsFiltersApplied({
      dashboard: dashboard.title,
      filters: draftFilters.map(({ field }) => field),
    });
  };

  /** Clears all filters. */
  const handleClearAll = () => {
    draftFilterSetters.clear();

    // We only clear the filters in the store if there previously were any filters applied, to avoid
    // an unnecessary state update that would cause the instrument data to be reloaded.
    if (filters.length) {
      dispatch(
        analyticsActions.setFilters({
          filters: [],
        }),
      );
      ampli.analyticsFiltersCleared({ dashboard: dashboard.title });
    }
  };

  /** Returns the enriched dashboard filter with values and operators. */
  const getDashboardFilter = useCallback(
    (filter: DashboardFilter): DashboardFilter =>
      data?.data.filters.find(({ field }) => field === filter.field) ?? filter,
    [data?.data.filters],
  );

  return (
    <div
      className={classNames(styles.filters, {
        [styles.filtersCollapsed]: isCollapsed,
      })}
    >
      {isFetching && <Spinner size={20} />}

      {isError && (
        <Tooltip content="An error occurred. Click to retry." dark>
          <FlexBox>
            <Icon
              className={styles.pointer}
              name="exclamation"
              color={colors.red['500']}
              size={20}
              onClick={readFilters}
            />
          </FlexBox>
        </Tooltip>
      )}

      <div className={styles.dropdowns}>
        {dashboardFilters.map((filter) => {
          if (filter.field === 'custom_fields') {
            if (!hasCustomFields) return null;

            const draftControls = draftFilters.filter(
              ({ field }) => field === 'custom_fields',
            ) as CustomAttributeFilterControl[];

            return (
              <AnalyticsCustomAttributesFilter
                key={filter.field}
                field="custom_fields"
                draftControls={draftControls}
                buttonProps={{
                  disabled: isFetching,
                  label: draftControls.length || undefined,
                }}
                onChange={handleChange}
              />
            );
          }

          const control = filters.find(
            ({ field }) => field === filter.field,
          ) as BasicFilterControl;

          const draftControl = draftFilters.find(
            ({ field }) => field === filter.field,
          ) as BasicFilterControl;

          const dashboardFilter = getDashboardFilter(filter);
          const assets = getFilterAssets(filter.field);

          return (
            <AnalyticsFilter<BasicFilterControl>
              key={filter.field}
              field={filter.field}
              availableValues={dashboardFilter.values}
              selectedValues={draftControl?.values}
              pinnedValues={control?.values}
              availableOperators={dashboardFilter.operators}
              selectedOperator={draftControl?.operator}
              isMultiple
              isSelectionDisabled={isOptionalOperator(draftControl?.operator)}
              hasDeterminateSelection
              onChange={handleChange}
              buttonProps={{
                variant: 'tertiary',
                label: draftControl
                  ? draftControl.values.length || 1
                  : undefined,
                disabled: isFetching,
              }}
            >
              {assets.label}
            </AnalyticsFilter>
          );
        })}
      </div>

      <Button
        className={classNames(styles.apply, {
          [styles.applyPulse]: isDirty,
        })}
        variant="primary"
        disabled={isFetching || !isDirty}
        onClick={handleApply}
      >
        Apply
      </Button>

      <IconButton
        variant="tertiary"
        icon="trash"
        disabled={!draftFilters.length}
        onClick={handleClearAll}
      />
    </div>
  );
});
